import React, { SetStateAction, useContext, useEffect, useMemo, useState } from "react";
import {  useNavigate } from "react-router-dom";
import MUIDataTable, { FilterType, MUIDataTableColumn, Responsive, SelectableRows } from "mui-datatables";
import { Button } from "@mui/material";
import { formatTime } from "../../utils";
import config, { DisplayablePlantingData, CsvDownloadUrl, ApiPlantingDataPagination } from "../../config";
import "./index.css";
import axios, { AxiosError } from "axios";
import downloadImage from "../../assets/images/download-icon.png";
import FrontEndContext from "../../context/FrontEndContext";
import { isTokenExpired, refreshBearerToken } from "../../services/auth";

const { getCSVPlantingExport, OPENPLANTINGSPAGINATION, API_FAIL_ERROR } = config?.api;

const CONFIG_STORAGE_KEY = "all-plantings";

interface FieldLogListPaginationProps {
  passedSystemIds?: string[];
  passedCompanyChanged?: boolean,
  onFinishedLoading?: () => void;
}

interface plantingData {
  id: string,
  systemId: string,
  systemName: string,
  farmId: string,
  farmName: string,
  fieldId: string,
  fieldName: string,
  varietyId: string,
  varietyName: string,
  seedlotId: string,
  seedlotName: string,
  seedSpacingId: string,
  space: string,
  companyId: string,
  companyName: string,
  creationTime: string,
  range: string,
  acreage: number,
  seedWeight: number,
  updateTime: string
}

interface plantingDataPagination {
  numberOfPages: number,
  currentPage: number,
  pagesLeft: number,
  pageSize: number
}

export default function FieldLogListPagination(props: FieldLogListPaginationProps) { 
  const { passedSystemIds, passedCompanyChanged, onFinishedLoading } = props; 
  //const[searchSystemId, setSearchSystemId] = useState<string>("");
  const navigate = useNavigate();
  const [plantings, setPlantings] = useState<DisplayablePlantingData[]>([]);
  const { t, language, dataLoaded, headersWithAuth,  setBearerToken, } = useContext(FrontEndContext);
  //Server side pagination changes
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalPageCount, setTotalPageCount] = useState(0);
  const [numberOfRecords, setNumberOfRecords] = useState(0);
  //Filters
  const [seedSensingSystemId, setSeedSensingSystemId] = useState();
  const [companyName, setCompanyName] = useState();
  const [systemId, setSystemId] = useState();
  const [farmName, setFarmName] = useState();
  const [fieldName, setFieldName] = useState();
  const [varietyName, setVarietyName] = useState();
  const [seedlotName, setSeedlotName] = useState();
  const [sortedColumn, setSortedColumn] = useState();
  const [sortedDirection, setSortedDirction] = useState();
  const [farmFilter, setFarmFilter] = useState<string[]>();


  const clearLocalStorageConfig = () => {
    localStorage.removeItem(CONFIG_STORAGE_KEY);
  };

  useEffect(() => {
    clearLocalStorageConfig();
  }, [language]);

  const downloadCSVFile = async () => {
    //debugger;
    try {
      //Get url to CSV file
      const { data: csvUrl}  = await axios.post<CsvDownloadUrl>(getCSVPlantingExport(), {}, headersWithAuth);
      
      //Get the csv file
      const response = await axios.get(csvUrl.logsUrl, {
        headers: headersWithAuth,
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `FieldLogs_ ${new Date().toISOString().replace(/[^0-9]/g, '').slice(0, -3)}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error(`Error downloading the file:`);
    }
    
    /*
    const link = document.createElement('a');
    link.href = csvUrl.logsUrl;
    
    link.download = 'Planting_Data.csv';

    // Append to html link element page
    document.body.appendChild(link);

    // Start download
    link.click();
    document.body.removeChild(link);
    //console.log("download csv logic goes here. " + csvUrl.logsUrl);
    */

  }


  const COLUMNS = useMemo<MUIDataTableColumn[]>(() => {
    const infoOptions = {
      setCellProps: () => ({
        align: "center" as const,
      }),
    };

    const infoOptionsNoFilter = {
      filter: false,
      setCellProps: () => ({
        align: "center" as const,
      }),
    };

    const viewOptions = {
      filter: false,
      viewColumns: false,
      customBodyRender: (value: any) => (
        <Button variant="contained" onClick={() => navigate(`/planting/${value}`)}>
          {t("view")}
        </Button>
      ),
    };

    const dateOptions = {
      customBodyRender: (value: any) => formatTime(value),
      setCellProps: () => ({
        align: "center" as const,
      }),
    };

    const dateOptionsNoFilter = {
      filter: false,
      customBodyRender: (value: any) => formatTime(value),
      setCellProps: () => ({
        align: "center" as const,
      }),
    };

    const uniqueFarms = ["albany 01", "emmits", "moase"];
    setFarmFilter(uniqueFarms);

    //const uniqueFarms = [...new Set(data.map(item => item.farmName))];
    //const uniqueVarieties = [...new Set(data.map(item => item.varietyName))];

    return [
      { label: " ", name: "systemAndPlantingId", options: viewOptions },
      { label: t("company").toUpperCase(), name: "companyName", options: { ...infoOptions, display: false } },
      { label: t("system").toUpperCase(), name: "systemName", options: infoOptions },
      { label: t("farm").toUpperCase(), name: "farmName", options:{...infoOptions,filterOptions: {names: uniqueFarms,},},}, //farmFilter
      { label: t("field").toUpperCase(), name: "fieldName", options: infoOptions },
      { label: t("variety").toUpperCase(), name: "varietyName", options: infoOptions },
      { label: t("seedlot").toUpperCase(), name: "seedlotName", options: infoOptions },
      { label: t("acreage").toUpperCase(), name: "acreage", options: infoOptionsNoFilter },
      { label: t("seedSpacing").toUpperCase(), name: "space", options: infoOptionsNoFilter },
      { label: t("seedWeight").toUpperCase(), name: "seedWeight", options: infoOptionsNoFilter },
      { label: t("lastUpdated").toUpperCase(), name: "updateTime", options: dateOptionsNoFilter },
    ];
  }, [t, navigate])


  const OPTIONS = {
    download: false,
    filterType: "dropdown" as FilterType,
    //pagination: true,
    rowsPerPageOptions: [10, 15, 25],
    //Pagination server side Changes. May 14 2025
    search: false,
    sort: true,
    serverSide: true,
    count: Number(numberOfRecords),
    rowsPerPage: rowsPerPage,
    page: page-1,//Table pages are 0 based. Need to subtract 1 from page from API to get correct page for table.
    onTableChange: (action: string, tableState: { page: any; rowsPerPage: SetStateAction<number>; filterList: any[]; sortOrder: any; }) => {
      if (action === "changePage") {
        setPage(Number(tableState.page) + 1);
      } else if (action === "changeRowsPerPage") {
        setRowsPerPage(tableState.rowsPerPage);
      } else if (action === "filterChange") {
        //Problem is current filters are names not id's
        setSeedSensingSystemId(tableState.filterList[0]?.[0] || "");
        setCompanyName(tableState.filterList[1]?.[0] || "");
        setSystemId(tableState.filterList[2]?.[0] || "");
        setFarmName(tableState.filterList[3]?.[0] || "");
        setFieldName(tableState.filterList[4]?.[0] || "");
        setVarietyName(tableState.filterList[5]?.[0] || "");
        setSeedlotName(tableState.filterList[6]?.[0] || "");
      } else if (action === 'sort'){
        setSortedColumn(tableState.sortOrder.name);
        setSortedDirction(tableState.sortOrder.direction);
      }      
    },
    print: false,
    customToolbar: () => (<img className="MuiButtonBase-root tss-10rusft-MUIDataTableToolbar-icon MuiIconButton-root MuiIconButton-sizeMedium css-78trlr-MuiButtonBase-root-MuiIconButton-root" 
        onClick={downloadCSVFile} data-testid="DownloadCSV-iconButton" aria-label="Download CSV" src={downloadImage} width="40px" height="40px">
      
    </img>),
    responsive: "standard" as Responsive,//DM April 1 changed vertical to scroll
    selectableRows: "single" as SelectableRows,
    selectableRowsHideCheckboxes: true,
    /*sortOrder: {
      name: "updateTime",
      direction: "desc" as any,
    },*/
    storageKey: CONFIG_STORAGE_KEY,
    textLabels: {
      body: {
        noMatch: dataLoaded ?t("noFieldLogsAvailable") :t("loading"),
        //noMatch: t("loading"),
      },
      pagination: {
        next: t("nextPage"),
        previous: t("previousPage"),
        rowsPerPage: t("rowsPerPage"),
        displayRows: t("displayRows"),
      },
      toolbar: {
        //search: t("searchTable"),
        downloadCsv: t("downloadCsv"),
        viewColumns: t("viewColumns"),
        filterTable: t("filterTable"),
      },
      filter: {
        all: t("tableFilterAll"),
        title: t("tableFilterTitle"),
        reset: t("tableFilterReset"),
      },
      viewColumns: {
        title: t("viewColumnsTitle"),
        titleAria: t("viewColumnsTitleAria"),
      },
    },
  };

  //setSearchSystemId(passedSystemId);

  useEffect(() => {
    if (!dataLoaded) {
      console.log("Is dataLoaded false? " + dataLoaded);
      return;
    }

    
    //Get data all systems for one farm.
    const loadLogPlantings = async () => {
      let currentToken = headersWithAuth?.headers?.Authorization.split(" ")[1] ?? "";
      let newToken = "";
      
      // Refresh the token if expired
      if (isTokenExpired(currentToken)) {
          newToken = await refreshBearerToken();
          if (newToken) {
              setBearerToken(newToken);
              currentToken = newToken;
          } else {
              navigate("/login");
              return;
          }
      }
      const updatedHeadersWithAuth = {
      headers: { Authorization: `Bearer ${newToken || currentToken}` },
      };
      let systemLogPlantings: DisplayablePlantingData[] = [];
      try {
            const open = await axios.post<ApiPlantingDataPagination>(OPENPLANTINGSPAGINATION, { pageNumber: passedCompanyChanged? 1: page,
                                                                                                pageSize: rowsPerPage, 
                                                                                                systemIds: passedSystemIds, 
                                                                                                //seedSensingSystemId: seedSensingSystemId,
                                                                                                companyName: companyName,
                                                                                                farmName: farmName,
                                                                                                fieldName: fieldName,
                                                                                                varietyName: varietyName,
                                                                                                seedlotName: seedlotName,
                                                                                                sortBy: sortedColumn,
                                                                                                sortDirection: sortedDirection,
                                                                                              }, 
                                                                                                updatedHeadersWithAuth);
            //convert fieldLogData into DisplayablePlantingData
            systemLogPlantings = open.data.plantings ? open.data.plantings.map((planting) => convertToDisplayable(planting)) : [];
            setPlantings(systemLogPlantings);
            setPage(open.data.pagination.currentPage);
            setRowsPerPage(open.data.pagination.pageSize);
            setTotalPageCount(open.data.pagination.numberOfPages);
            setNumberOfRecords(open.data.pagination.numberOfRecords);
            onFinishedLoading?.(); // Only used by admin. Used to identify if new company or not. This allows scrolling pages for a specific company.
            //setFarmFilter(farmName); Don't think I need this for custom filtering.

          }
      catch (error) {
        console.log(`[Plantings] ${API_FAIL_ERROR}${error as AxiosError}`);
      }
    };
    loadLogPlantings();
    
    const convertToDisplayable = (
          planting: plantingData,
        ): DisplayablePlantingData => {
          if (!planting) {
            return undefined as unknown as DisplayablePlantingData;
          }
    
          const {  companyName, systemId, systemName, id, range, acreage, seedWeight, updateTime, fieldName,farmName, varietyName, seedlotName, space, } = planting;
          // calculating the `id` field as a combination of the systemId and the id of the planting
          // separated by a slash is important as this value is used as the last part of the URL that
          // is used to access the planting or the live data.
          // This is also used to ensure that the table has a unique ID that encompasses the system Id
          // and the planting id. Just in case the planting id is the same on different systems.
          const systemAndPlantingId = `${systemId}/${id}`;
          let idNumber = Number(id);
          let rangeNumber = Number(range);
          let spaceNumber = Number(space);
          return {
            systemId: systemId,
            systemAndPlantingId,
            id: idNumber,
            companyName,
            systemName,
            type: 'OPEN',
            farmName: farmName,
            fieldName: fieldName,
            varietyName: varietyName,
            seedlotName: seedlotName,
            range: rangeNumber,
            acreage: acreage,
            space: spaceNumber,
            seedWeight: seedWeight,
            updateTime: updateTime,
          };
        };

    //DM May 4 2024. Every 30 min refresh log planting table.
    const intervalId = setInterval(() => {
        loadLogPlantings();
    }, 900000); // Every 15 minutes
    return () => clearInterval(intervalId); // Clean up on unmount
    
  }, [dataLoaded, page, rowsPerPage, companyName, farmName, fieldName, varietyName, seedlotName,  sortedColumn, sortedDirection, passedSystemIds, passedCompanyChanged, onFinishedLoading]); //acreage, seedWeight, seedSpacing, updatedAfter, 

  return (
    <div className="field-log-table">
      <MUIDataTable title={t("fieldLogs")} data={plantings} columns={COLUMNS} options={OPTIONS} />
    </div>
  );
}
